import { styled } from "@mui/material/styles";
import { FlexRowCenter, Text } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import AutoCompleteSelect from "designSystem/components/select/AutoCompleteSelect";
import { NO_WRAP } from "designSystem/styles/style-functions";
import { ComponentItem } from "designSystem/types/component-item-types";
import { useCompareStore } from "csi/page/ComparePageStore";
import { getSearch } from "csi/service/dataStoreService";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, useEffect, useState } from "react";
import BOMDetailsTooltip from "src/Specification/BOM/BOMDetailsTooltip";
import ChipSelectPhase from "src/Specification/BOM/ChipSelectPhase";
import ChipSelectRevision from "src/Specification/BOM/ChipSelectRevision";
import ChipState from "src/Specification/BOM/ChipState";

const PREFIX = "BOMRevisionBreadcrumb";

const classes = {
	breadcrumb: `${PREFIX}breadcrumb`,
	onlyWhiteSpaceNoWrap: `${PREFIX}onlyWhiteSpaceNoWrap`,
	productNodeTextBox: `${PREFIX}productNodeTextBox`,
};

const StyledFlexRowCenter = styled(FlexRowCenter)((
	{
		theme,
	},
) => ({
	[`&.${classes.breadcrumb}`]: {
		gap: 8,
		maxWidth: "calc(100% - 16px)",
	},

	[`& .${classes.onlyWhiteSpaceNoWrap}`]: {
		whiteSpace: "nowrap",
	},

	[`& .${classes.productNodeTextBox}`]: {
		flex: 4,
		minWidth: 30,
		...NO_WRAP,
	},
}));

interface BOMRevisionBreadcrumbProps {
	/** flag to indicate compare menu */
	isCompareMenu?: boolean;

	enableAutoCompleteSelect?: boolean;
}

export const BOMRevisionBreadcrumb: FunctionComponent<BOMRevisionBreadcrumbProps> = observer((props) => {
	const {
		compareNode,
		compareRevisionNode,
		compareProductAncestorNodes,
		compareProductNode,
		currentNode,
		currentRevisionNode,
		currentProductAncestorNodes,
		currentProductNode,
		getChipStateColorClass,
		getChipStateLabel,
		isCompareSampleBOM,
		isCurrentSampleBOM,
		onChangeBOM,
		onSelectRevision,
		onSelectRevisionAll,
	} = useCompareStore();

	const { isCompareMenu, enableAutoCompleteSelect } = props;

	let bomNode = currentNode;
	let isSampleBom = isCurrentSampleBOM;
	let revisionNode = currentRevisionNode;
	let productNode = currentProductNode;
	let productAncestorNodes = currentProductAncestorNodes;
	if (isCompareMenu) {
		bomNode = compareNode;
		isSampleBom = isCompareSampleBOM;
		productAncestorNodes = compareProductAncestorNodes;
		productNode = compareProductNode;
		revisionNode = compareRevisionNode;
	}

	const loc = useLocale();

	// get options on mount
	const [options, setOptions] = useState([]);
	useEffect(() => {
		async function go() {
			const baseSearch = getSearch(`${bomNode.$Type}.${productNode.$Type}`, productNode.$URL);
			const response = await baseSearch.execute();
			const nodes = response.getResultNodes();
			const options: ComponentItem[] = nodes.map((node, index) => {
				return {
					id: node.$URL,
					node,
				};
			});
			setOptions(options);
		}
		go();
	}, [productNode.$URL]);

	const chipStateClassName = getChipStateColorClass(revisionNode);

	const [searching, setSearching] = useState(false);

	const bomNodeName = loc.getNodeName(bomNode);

	const selectedOption = options.find((x) => loc.getComponentItemName(x) === bomNodeName);

	const {
		breadcrumb,
		onlyWhiteSpaceNoWrap,
		productNodeTextBox,
	} = classes;

	return (
		<StyledFlexRowCenter className={breadcrumb} mx={4}>
			<Text className={onlyWhiteSpaceNoWrap} text="... /" />
			<BOMDetailsTooltip ancestorNodes={productAncestorNodes} productNode={productNode}>
				<Text
					className={productNodeTextBox}
					text={loc.getNodeName(productNode)}
				/>
			</BOMDetailsTooltip>
			<Text className={onlyWhiteSpaceNoWrap} text="/" />
			<AutoCompleteSelect
				enableSearch={enableAutoCompleteSelect}
				fontSize={12}
				labelText={bomNodeName}
				onBlur={() => setSearching(false)}
				onChange={(event, item) => onChangeBOM(item, isCompareMenu)}
				onClickName={() => setSearching(true)}
				options={options}
				searchValue={selectedOption}
				searchWidth={184}
				searching={searching}
			/>
			{!isSampleBom && (
				<>
					<ChipSelectPhase
						isCompareMenu={isCompareMenu}
						onSelectRevision={onSelectRevision}
					/>
					<ChipState
						className={chipStateClassName}
						isCompareMenu={isCompareMenu}
						label={getChipStateLabel(revisionNode)}
					/>
					<ChipSelectRevision
						isCompareMenu={isCompareMenu}
						onSelectRevision={onSelectRevision}
						onSelectRevisionAll={onSelectRevisionAll}
					/>
				</>
			)}
		</StyledFlexRowCenter>
	);
});

export default BOMRevisionBreadcrumb;